import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useProducts } from '../../hooks/useProducts';
import { useInventory } from '../../hooks/useInventory';
import { CreateInventoryMovementData, InventoryMovementType } from '../../types/warehouse';
import { Product } from '../../types/product';
import LoadingScreen from '../common/LoadingScreen';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ArrowsRightLeftIcon,
  CubeIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

interface StockTransferFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preSelectedProductId?: string;
  preSelectedFromWarehouseId?: string;
}

const StockTransferForm: React.FC<StockTransferFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedProductId,
  preSelectedFromWarehouseId
}) => {
  const { t } = useLanguage();
  const { warehouses, loading: loadingWarehouses } = useWarehouses();
  const { products, loading: loadingProducts } = useProducts();
  const { inventory, fetchInventory, createInventoryMovement } = useInventory();
  
  const [formData, setFormData] = useState<{
    productId: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    quantity: number;
    note: string;
    documentNumber: string;
  }>({
    productId: preSelectedProductId || '',
    fromWarehouseId: preSelectedFromWarehouseId || '',
    toWarehouseId: '',
    quantity: 0,
    note: '',
    documentNumber: ''
  });

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [availableQuantity, setAvailableQuantity] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Raktár keresés state
  const [fromWarehouseSearchTerm, setFromWarehouseSearchTerm] = useState('');
  const [showFromWarehouseSearch, setShowFromWarehouseSearch] = useState(false);
  const [selectedFromWarehouse, setSelectedFromWarehouse] = useState<any>(null);

  const [toWarehouseSearchTerm, setToWarehouseSearchTerm] = useState('');
  const [showToWarehouseSearch, setShowToWarehouseSearch] = useState(false);
  const [selectedToWarehouse, setSelectedToWarehouse] = useState<any>(null);

  // Get selected product
  const selectedProduct = products.find(p => p.id === formData.productId);

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length < 2) {
        setFilteredProducts([]);
        return;
      }

      setSearchLoading(true);
      
      // Simulate async behavior for smooth UX
      setTimeout(() => {
        const results = products.filter(product =>
          product.isActive && (
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.code?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        ).slice(0, 20); // Limit to 20 results for performance
        
        setFilteredProducts(results);
        setSearchLoading(false);
      }, 100);
    },
    [products]
  );

  // Debounce search calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showProductSearch && !selectedProduct) {
        debouncedSearch(productSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [productSearchTerm, showProductSearch, selectedProduct, debouncedSearch]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        productId: preSelectedProductId || '',
        fromWarehouseId: preSelectedFromWarehouseId || '',
        toWarehouseId: '',
        quantity: 0,
        note: '',
        documentNumber: ''
      });
      setProductSearchTerm('');
      setShowProductSearch(false);
      setFromWarehouseSearchTerm('');
      setShowFromWarehouseSearch(false);
      setSelectedFromWarehouse(preSelectedFromWarehouseId ? warehouses.find(w => w.id === preSelectedFromWarehouseId) : null);
      setToWarehouseSearchTerm('');
      setShowToWarehouseSearch(false);
      setSelectedToWarehouse(null);
    }
  }, [isOpen, preSelectedProductId, preSelectedFromWarehouseId, warehouses]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close product search if clicked outside
      if (showProductSearch && !target.closest('.product-search-container')) {
        setShowProductSearch(false);
      }
      
      // Close from warehouse search if clicked outside
      if (showFromWarehouseSearch && !target.closest('.from-warehouse-search-container')) {
        setShowFromWarehouseSearch(false);
      }
      
      // Close to warehouse search if clicked outside
      if (showToWarehouseSearch && !target.closest('.to-warehouse-search-container')) {
        setShowToWarehouseSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductSearch, showFromWarehouseSearch, showToWarehouseSearch]);

  // Get available quantity when product and from warehouse changes
  useEffect(() => {
    if (formData.productId && selectedFromWarehouse?.id) {
      const inventoryItem = inventory.find(
        item => item.productId === formData.productId && item.warehouseId === selectedFromWarehouse.id
      );
      const available = inventoryItem ? 
        (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
      setAvailableQuantity(Math.max(0, available));
    } else {
      setAvailableQuantity(0);
    }
  }, [formData.productId, selectedFromWarehouse, inventory]);

  // Load inventory when form opens
  useEffect(() => {
    if (isOpen) {
      fetchInventory({});
    }
  }, [isOpen, fetchInventory]);

  // Get warehouses with stock for the selected product
  const warehousesWithStock = selectedProduct ? 
    warehouses.filter(warehouse => {
      const inventoryItem = inventory.find(
        item => item.productId === formData.productId && item.warehouseId === warehouse.id
      );
      const availableStock = inventoryItem ? 
        (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
      return warehouse.isActive && availableStock > 0;
    }) : [];

  // Get available warehouses (exclude the selected from warehouse)
  const availableToWarehouses = warehouses.filter(w => 
    w.isActive && w.id !== selectedFromWarehouse?.id
  );

  const availableFromWarehouses = formData.productId ? warehousesWithStock : warehouses.filter(w => w.isActive);

  // Filter warehouses based on search terms
  const filteredFromWarehouses = availableFromWarehouses.filter(warehouse =>
    warehouse.name.toLowerCase().includes(fromWarehouseSearchTerm.toLowerCase()) ||
    warehouse.code.toLowerCase().includes(fromWarehouseSearchTerm.toLowerCase())
  );

  const filteredToWarehouses = availableToWarehouses.filter(warehouse =>
    warehouse.name.toLowerCase().includes(toWarehouseSearchTerm.toLowerCase()) ||
    warehouse.code.toLowerCase().includes(toWarehouseSearchTerm.toLowerCase())
  );

  // Auto-select from warehouse if there's only one option with stock
  useEffect(() => {
    if (formData.productId && warehousesWithStock.length === 1 && !selectedFromWarehouse) {
      const warehouse = warehousesWithStock[0];
      setSelectedFromWarehouse(warehouse);
      setFormData(prev => ({ ...prev, fromWarehouseId: warehouse.id }));
      setFromWarehouseSearchTerm(`${warehouse.name} (${warehouse.code})`);
    }
  }, [formData.productId, warehousesWithStock, selectedFromWarehouse]);

  const selectFromWarehouse = (warehouse: any) => {
    setSelectedFromWarehouse(warehouse);
    setFormData(prev => ({ 
      ...prev, 
      fromWarehouseId: warehouse.id,
      // Reset to warehouse if it's the same as from warehouse
      toWarehouseId: selectedToWarehouse?.id === warehouse.id ? '' : prev.toWarehouseId
    }));
    if (selectedToWarehouse?.id === warehouse.id) {
      setSelectedToWarehouse(null);
      setToWarehouseSearchTerm('');
    }
    setFromWarehouseSearchTerm(`${warehouse.name} (${warehouse.code})`);
    setShowFromWarehouseSearch(false);
  };

  const selectToWarehouse = (warehouse: any) => {
    setSelectedToWarehouse(warehouse);
    setFormData(prev => ({ ...prev, toWarehouseId: warehouse.id }));
    setToWarehouseSearchTerm(`${warehouse.name} (${warehouse.code})`);
    setShowToWarehouseSearch(false);
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset from warehouse when product changes
      ...(field === 'productId' && { fromWarehouseId: '' }),
      // Reset quantity when product or from warehouse changes
      ...(field === 'productId' || field === 'fromWarehouseId') && { quantity: 0 }
    }));
  };

  const handleProductSelect = (product: Product) => {
    setFormData(prev => ({
      ...prev,
      productId: product.id,
      fromWarehouseId: '', // Reset forrás raktár
      toWarehouseId: '',   // Reset cél raktár is
      quantity: 0          // Reset mennyiség
    }));
    setProductSearchTerm(product.name);
    setShowProductSearch(false);
    
    // Reset warehouse selections
    setSelectedFromWarehouse(null);
    setSelectedToWarehouse(null);
    setFromWarehouseSearchTerm('');
    setToWarehouseSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.productId || !selectedFromWarehouse || !selectedToWarehouse) {
      toast.error('Kérlek töltsd ki az összes kötelező mezőt!');
      return;
    }

    if (formData.quantity <= 0) {
      toast.error('A mennyiségnek pozitív számnak kell lennie!');
      return;
    }

    if (formData.quantity > availableQuantity) {
      toast.error(`Nincs elegendő készlet! Elérhető: ${availableQuantity}`);
      return;
    }

    setLoading(true);
    try {
      const movementData: CreateInventoryMovementData = {
        productId: formData.productId,
        warehouseId: selectedFromWarehouse.id,
        targetWarehouseId: selectedToWarehouse.id,
        quantity: formData.quantity,
        type: InventoryMovementType.STOCK_TRANSFER,
        documentNumber: formData.documentNumber || undefined,
        documentType: 'TRANSFER',
        note: formData.note || undefined
      };

      await createInventoryMovement(movementData);
      toast.success('Átmozgatás sikeresen rögzítve!');
      
      // Lista frissítése - kis késés a Firebase persistálás miatt
      setTimeout(() => {
        console.log('Attempting to refresh inventory movements list after transfer');
        if ((window as any).refreshInventoryMovements) {
          (window as any).refreshInventoryMovements();
        } else {
          console.warn('refreshInventoryMovements function not found on window');
        }
      }, 500);
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Hiba az átmozgatás rögzítésekor:', error);
      toast.error('Hiba történt az átmozgatás rögzítésekor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Raktárak közötti átmozgatás"
      size="6xl"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Termék kiválasztása */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 dark:from-purple-900/30 dark:to-fuchsia-900/30 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-700">
                <CubeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Termék kiválasztása</h3>
                
                {selectedProduct ? (
                  <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{selectedProduct.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Kód: {selectedProduct.code}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, productId: '' }));
                        setProductSearchTerm('');
                        setShowProductSearch(true);
                      }}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                    >
                      Módosítás
                    </button>
                  </div>
                ) : (
                  <div className="relative product-search-container">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={productSearchTerm}
                        onChange={(e) => {
                          setProductSearchTerm(e.target.value);
                          setShowProductSearch(true);
                        }}
                        onFocus={() => setShowProductSearch(true)}
                        placeholder="Termék keresése név vagy kód alapján..."
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    
                    {showProductSearch && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {searchLoading ? (
                          <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                            Keresés...
                          </div>
                        ) : productSearchTerm.length >= 2 ? (
                          filteredProducts.length > 0 ? (
                            filteredProducts.map((product) => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => handleProductSelect(product)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Kód: {product.code} - <span className={(() => {
                                    const totalStock = inventory
                                      .filter(item => item.productId === product.id)
                                      .reduce((sum, item) => sum + (item.quantity - (item.reservedQuantity || 0)), 0);
                                    return totalStock > 0
                                      ? 'text-green-600 dark:text-green-400 font-medium'
                                      : 'text-red-600 dark:text-red-400 font-medium';
                                  })()}>
                                    Összes készlet: {(() => {
                                    const totalStock = inventory
                                      .filter(item => item.productId === product.id)
                                      .reduce((sum, item) => sum + (item.quantity - (item.reservedQuantity || 0)), 0);
                                    return `${totalStock} ${product.unit || 'db'}`;
                                    })()}
                                  </span> raktárakban
                                </div>
                              </button>
                            ))
                          ) : (
                                                      <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                              Nincs találat
                            </div>
                          )
                        ) : productSearchTerm.length > 0 ? (
                          <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                            Írj be legalább 2 karaktert a kereséshez
                          </div>
                        ) : (
                          products.filter(p => p.isActive).slice(0, 10).map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              onClick={() => handleProductSelect(product)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                Kód: {product.code} - <span className={(() => {
                                  const totalStock = inventory
                                    .filter(item => item.productId === product.id)
                                    .reduce((sum, item) => sum + (item.quantity - (item.reservedQuantity || 0)), 0);
                                  return totalStock > 0
                                    ? 'text-green-600 dark:text-green-400 font-medium'
                                    : 'text-red-600 dark:text-red-400 font-medium';
                                })()}>
                                  Összes készlet: {(() => {
                                  const totalStock = inventory
                                    .filter(item => item.productId === product.id)
                                    .reduce((sum, item) => sum + (item.quantity - (item.reservedQuantity || 0)), 0);
                                  return `${totalStock} ${product.unit || 'db'}`;
                                  })()}
                                </span> raktárakban
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Raktárak kiválasztása */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <BuildingStorefrontIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Raktárak kiválasztása</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Forrás raktár */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Forrás raktár *
                    </label>
                    {selectedFromWarehouse ? (
                      <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{selectedFromWarehouse.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Kód: {selectedFromWarehouse.code}
                            {formData.productId && (
                              <>
                                {` - Készlet: `}
                                <span className={(() => {
                                  const inventoryItem = inventory.find(
                                    item => item.productId === formData.productId && item.warehouseId === selectedFromWarehouse.id
                                  );
                                  const availableStock = inventoryItem ? 
                                    (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                  return availableStock <= 0 ? 'text-red-600 font-medium' : 'text-green-600 dark:text-green-400 font-medium';
                                })()}>
                                  {(() => {
                                    const inventoryItem = inventory.find(
                                      item => item.productId === formData.productId && item.warehouseId === selectedFromWarehouse.id
                                    );
                                    const availableStock = inventoryItem ? 
                                      (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                    return `${availableStock} ${selectedProduct?.unit || 'db'}`;
                                  })()}
                                </span>
                              </>
                            )}
                          </div>
                          {formData.productId && (() => {
                            const inventoryItem = inventory.find(
                              item => item.productId === formData.productId && item.warehouseId === selectedFromWarehouse.id
                            );
                            const availableStock = inventoryItem ? 
                              (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                            return availableStock <= 0 && (
                              <div className="text-sm text-red-600 font-medium mt-1">
                                ⚠️ Nincs elérhető készlet ebben a raktárban!
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFromWarehouse(null);
                            setFormData(prev => ({ ...prev, fromWarehouseId: '', quantity: 0 }));
                            setFromWarehouseSearchTerm('');
                            setShowFromWarehouseSearch(true);
                          }}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          Módosítás
                        </button>
                      </div>
                    ) : (
                      <div className="relative from-warehouse-search-container">
                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={fromWarehouseSearchTerm}
                          onChange={(e) => {
                            setFromWarehouseSearchTerm(e.target.value);
                            setShowFromWarehouseSearch(true);
                          }}
                          onFocus={() => setShowFromWarehouseSearch(true)}
                          placeholder={formData.productId ? "Forrás raktár keresése (csak készlettel rendelkezők)..." : "Először válassz terméket..."}
                          disabled={!formData.productId}
                          className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                        />
                        
                        {showFromWarehouseSearch && formData.productId && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {fromWarehouseSearchTerm.length > 0 
                              ? filteredFromWarehouses.length > 0 
                                ? filteredFromWarehouses.map((warehouse) => {
                                    const inventoryItem = inventory.find(
                                      item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                    );
                                    const availableStock = inventoryItem ? 
                                      (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                    
                                    return (
                                      <button
                                        key={warehouse.id}
                                        type="button"
                                        onClick={() => selectFromWarehouse(warehouse)}
                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                      >
                                        <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                          Kód: {warehouse.code} - <span className={(() => {
                                            const inventoryItem = inventory.find(
                                              item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                            );
                                            const availableStock = inventoryItem ? 
                                              (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                            return availableStock > 0
                                              ? 'text-green-600 dark:text-green-400 font-medium'
                                              : 'text-red-600 dark:text-red-400 font-medium';
                                          })()}>
                                            Készlet: {(() => {
                                              const inventoryItem = inventory.find(
                                                item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                              );
                                              const availableStock = inventoryItem ? 
                                                (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                              return `${availableStock} ${selectedProduct?.unit || 'db'}`;
                                            })()}
                                          </span>
                                        </div>
                                      </button>
                                    );
                                  })
                                : (
                                    <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                                      Nincs találat
                                    </div>
                                  )
                              : availableFromWarehouses.slice(0, 10).map((warehouse) => {
                                  const inventoryItem = inventory.find(
                                    item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                  );
                                  const availableStock = inventoryItem ? 
                                    (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                  
                                  return (
                                    <button
                                      key={warehouse.id}
                                      type="button"
                                      onClick={() => selectFromWarehouse(warehouse)}
                                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                    >
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                      <div className="text-sm text-gray-500 dark:text-gray-400">
                                        Kód: {warehouse.code} - <span className={(() => {
                                          const inventoryItem = inventory.find(
                                            item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                          );
                                          const availableStock = inventoryItem ? 
                                            (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                          return availableStock > 0
                                            ? 'text-green-600 dark:text-green-400 font-medium'
                                            : 'text-red-600 dark:text-red-400 font-medium';
                                        })()}>
                                          Készlet: {(() => {
                                            const inventoryItem = inventory.find(
                                              item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                            );
                                            const availableStock = inventoryItem ? 
                                              (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                            return `${availableStock} ${selectedProduct?.unit || 'db'}`;
                                          })()}
                                        </span>
                                      </div>
                                    </button>
                                  );
                                })
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Cél raktár */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cél raktár *
                    </label>
                    {selectedToWarehouse ? (
                      <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{selectedToWarehouse.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Kód: {selectedToWarehouse.code}
                            {formData.productId && (() => {
                              const inventoryItem = inventory.find(
                                item => item.productId === formData.productId && item.warehouseId === selectedToWarehouse.id
                              );
                              const currentStock = inventoryItem ? 
                                (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                              return (
                                <>
                                  {' - '}
                                  <span className={
                                    currentStock > 0
                                      ? 'text-green-600 dark:text-green-400 font-medium'
                                      : 'text-red-600 dark:text-red-400 font-medium'
                                  }>
                                    Jelenlegi készlet: {currentStock} {selectedProduct?.unit || 'db'}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedToWarehouse(null);
                            setFormData(prev => ({ ...prev, toWarehouseId: '' }));
                            setToWarehouseSearchTerm('');
                            setShowToWarehouseSearch(true);
                          }}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          Módosítás
                        </button>
                      </div>
                    ) : (
                      <div className="relative to-warehouse-search-container">
                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={toWarehouseSearchTerm}
                          onChange={(e) => {
                            setToWarehouseSearchTerm(e.target.value);
                            setShowToWarehouseSearch(true);
                          }}
                          onFocus={() => setShowToWarehouseSearch(true)}
                          placeholder="Cél raktár keresése..."
                          className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                        
                        {showToWarehouseSearch && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {toWarehouseSearchTerm.length > 0 
                              ? filteredToWarehouses.length > 0 
                                ? filteredToWarehouses.map((warehouse) => (
                                    <button
                                      key={warehouse.id}
                                      type="button"
                                      onClick={() => selectToWarehouse(warehouse)}
                                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                    >
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                      <div className="text-sm text-gray-500 dark:text-gray-400">
                                        Kód: {warehouse.code}
                                        {formData.productId && (() => {
                                          const inventoryItem = inventory.find(
                                            item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                          );
                                          const currentStock = inventoryItem ? 
                                            (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                          return (
                                            <>
                                              {' - '}
                                              <span className={
                                                currentStock > 0
                                                  ? 'text-green-600 dark:text-green-400 font-medium'
                                                  : 'text-red-600 dark:text-red-400 font-medium'
                                              }>
                                                Jelenlegi készlet: {currentStock} {selectedProduct?.unit || 'db'}
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </button>
                                  ))
                                : (
                                    <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                                      Nincs találat
                                    </div>
                                  )
                              : availableToWarehouses.slice(0, 10).map((warehouse) => (
                                  <button
                                    key={warehouse.id}
                                    type="button"
                                    onClick={() => selectToWarehouse(warehouse)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      Kód: {warehouse.code}
                                      {formData.productId && (() => {
                                        const inventoryItem = inventory.find(
                                          item => item.productId === formData.productId && item.warehouseId === warehouse.id
                                        );
                                        const currentStock = inventoryItem ? 
                                          (inventoryItem.quantity - (inventoryItem.reservedQuantity || 0)) : 0;
                                        return (
                                          <>
                                            {' - '}
                                            <span className={
                                              currentStock > 0
                                                ? 'text-green-600 dark:text-green-400 font-medium'
                                                : 'text-red-600 dark:text-red-400 font-medium'
                                            }>
                                              Jelenlegi készlet: {currentStock} {selectedProduct?.unit || 'db'}
                                            </span>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </button>
                                ))
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Átmozgatás vizualizáció */}
                {selectedFromWarehouse && selectedToWarehouse && (
                  <div className="mt-4 flex items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <BuildingStorefrontIcon className="h-8 w-8 text-blue-600 mx-auto mb-1" />
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedFromWarehouse.name}</div>
                      </div>
                      <ArrowRightIcon className="h-6 w-6 text-blue-600" />
                      <div className="text-center">
                        <BuildingStorefrontIcon className="h-8 w-8 text-blue-600 mx-auto mb-1" />
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedToWarehouse.name}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mennyiség */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <ScaleIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Mennyiség megadása</h3>
                
                <div className="relative">
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className={`w-full bg-white dark:bg-gray-700 border rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:ring-1 ${
                               availableQuantity <= 0 && formData.productId && selectedFromWarehouse
                                 ? 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500' 
                                 : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
                             }`}
                    placeholder="Adja meg az átmozgatandó mennyiséget..."
                    required
                  />
                  {availableQuantity > 0 && (
                    <div className="absolute right-3 top-2 text-sm text-gray-500 dark:text-gray-400">
                      Elérhető: {availableQuantity} {selectedProduct?.unit || 'db'}
                    </div>
                  )}
                </div>
                
                {availableQuantity === 0 && formData.productId && formData.fromWarehouseId && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    Nincs elérhető készlet a kiválasztott raktárban
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Bizonylat szám */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <DocumentTextIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Bizonylat adatok</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bizonylat szám
                  </label>
                  <input
                    type="text"
                    value={formData.documentNumber}
                    onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                    placeholder="pl. AT-2024-001"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Megjegyzés */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-yellow-100 to-amber-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <ClipboardDocumentListIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Megjegyzés</h3>
                
                <textarea
                  value={formData.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  rows={3}
                  placeholder="Opcionális megjegyzés az átmozgatáshoz..."
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                           placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Gombok */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading || !formData.productId || !selectedFromWarehouse || !selectedToWarehouse || formData.quantity <= 0 || availableQuantity <= 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 mr-2">
                    <LoadingScreen />
                  </div>
                  <span>Rögzítés...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Átmozgatás rögzítése
                </>
              )}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default StockTransferForm; 